import { useCallback, useEffect } from 'react';

// import { useNavigate } from 'react-router'
import { useStore } from '@/store';

interface ShortcutConfig {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  meta?: boolean;
  action: () => void;
  description?: string;
  preventDefault?: boolean;
}

/**
 * Hook for managing keyboard shortcuts
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts([
 *   {
 *     key: 'k',
 *     meta: true,
 *     action: () => openCommandPalette(),
 *     description: 'Open command palette'
 *   },
 *   {
 *     key: 'n',
 *     meta: true,
 *     action: () => createNewItem(),
 *     description: 'Create new item'
 *   }
 * ])
 * ```
 */
export function useKeyboardShortcuts(shortcuts: ShortcutConfig[], enabled = true) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.contentEditable === 'true';

      // Allow some shortcuts even in inputs (like Cmd+K)
      const allowInInput = event.metaKey || event.ctrlKey;

      if (isInput && !allowInInput) return;

      for (const shortcut of shortcuts) {
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatch = shortcut.ctrl ? event.ctrlKey || event.metaKey : true;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const metaMatch = shortcut.meta ? event.metaKey : true;

        if (keyMatch && ctrlMatch && altMatch && shiftMatch && metaMatch) {
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
          }
          shortcut.action();
          break;
        }
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
}

/**
 * Global keyboard shortcuts for the application
 */
export function useGlobalKeyboardShortcuts() {
  // const navigate = useNavigate()
  const setCommandPaletteOpen = useStore((state) => state.setCommandPaletteOpen);
  const toggleSidebar = useStore((state) => state.toggleSidebar);

  const shortcuts: ShortcutConfig[] = [
    // Command palette
    {
      key: 'k',
      meta: true,
      action: () => setCommandPaletteOpen(true),
      description: 'Open command palette',
    },
    // Quick navigation
    // {
    //   key: 'g',
    //   meta: true,
    //   shift: true,
    //   action: () => navigate('/dashboard'),
    //   description: 'Go to dashboard',
    // },
    // {
    //   key: 's',
    //   meta: true,
    //   shift: true,
    //   action: () => navigate('/submissions'),
    //   description: 'Go to submissions',
    // },
    // {
    //   key: 's',
    //   meta: true,
    //   shift: true,
    //   action: () => navigate('/settings'),
    //   description: 'Go to settings',
    // },
    // UI controls
    {
      key: 'b',
      meta: true,
      action: () => toggleSidebar(),
      description: 'Toggle sidebar',
    },
    // Create new
    // Removed create campaign shortcut for MVP
    // Search
    {
      key: '/',
      action: () => setCommandPaletteOpen(true),
      description: 'Open search',
      preventDefault: true,
    },
    // Help
    {
      key: '?',
      shift: true,
      action: () => showKeyboardShortcuts(),
      description: 'Show keyboard shortcuts',
    },
  ];

  useKeyboardShortcuts(shortcuts);
}

/**
 * Show keyboard shortcuts modal
 */
function showKeyboardShortcuts() {
  // You could open a modal here showing all shortcuts
  // For now, we'll just log them
  console.log('Keyboard Shortcuts:');
  console.log('⌘K - Open command palette');
  console.log('⌘B - Toggle sidebar');
  console.log('/ - Open search');
  console.log('? - Show keyboard shortcuts');
}

/**
 * Hook for handling escape key
 */
export function useEscapeKey(onEscape: () => void, enabled = true) {
  useKeyboardShortcuts(
    [
      {
        key: 'Escape',
        action: onEscape,
        description: 'Close/Cancel',
      },
    ],
    enabled
  );
}

/**
 * Hook for handling enter key
 */
export function useEnterKey(onEnter: () => void, enabled = true) {
  useKeyboardShortcuts(
    [
      {
        key: 'Enter',
        action: onEnter,
        description: 'Confirm/Submit',
      },
    ],
    enabled
  );
}

/**
 * Hook for arrow key navigation
 */
export function useArrowKeyNavigation(
  onUp: () => void,
  onDown: () => void,
  onLeft?: () => void,
  onRight?: () => void,
  enabled = true
) {
  const shortcuts: ShortcutConfig[] = [
    {
      key: 'ArrowUp',
      action: onUp,
      description: 'Navigate up',
    },
    {
      key: 'ArrowDown',
      action: onDown,
      description: 'Navigate down',
    },
  ];

  if (onLeft) {
    shortcuts.push({
      key: 'ArrowLeft',
      action: onLeft,
      description: 'Navigate left',
    });
  }

  if (onRight) {
    shortcuts.push({
      key: 'ArrowRight',
      action: onRight,
      description: 'Navigate right',
    });
  }

  useKeyboardShortcuts(shortcuts, enabled);
}
